import {
  Alert,
  Button,
  Flex,
  Heading,
  IconAddLine,
  Spinner,
  Text,
} from "@instructure/ui";
import { t } from "i18next";
import { useContext, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useLocation, useOutletContext } from "react-router-dom";
import {
  ERROR,
  useNotification,
} from "../../../../../components/CustomHooks/NotificationProvider";
import CustomRTE, {
  EMPTY_RTE,
} from "../../../../../components/CustomTextArea/CustomRTE";
import ProgressStepper from "../../../../../components/ProgressStepper";
import {
  upsertReflection,
  upsertReflectionDraft,
  upsertReflectionDraftFromTemplate,
  upsertReflectionFromTemplate,
} from "../../../../../components/Reflection/utils";
import { MULTI, Selector } from "../../../../../components/Selectors/Selector";
import { UserDetailContext } from "../../../../../utils/Context";
import { getFinalizeSteps } from "../utils";
import ReflectionCard from "./ReflectionCard";

const FinalizeReflections = () => {
  const showNotification = useNotification();
  const userDetails = useContext(UserDetailContext);
  const location = useLocation();
  const { reflections, setReflections } = useOutletContext();
  const { section, sectionRefTemplate } = location.state;
  const [publishing, setPublishing] = useState(false);
  const [creatingDraft, setCreatingDraft] = useState(false);
  const [selectedAuthors, setSelectedAuthors] = useState([]);

  const [reflectionText, setReflectionText] = useState("");
  const [sections, setSections] = useState(
    sectionRefTemplate?.sections?.map((section) => ({
      ...section,
      content: EMPTY_RTE,
    }))
  );
  const [clear, setClear] = useState(false);

  const authorSelections = reflections
    ? Array.from(
        new Set(
          reflections
            .filter((r) => !!r.metadata.author)
            .map((r) => r.metadata.author)
        )
      ).map((author) => ({ label: author, id: author }))
    : [];

  function handleReflectionChange(value) {
    setReflectionText(value);
  }

  function handleAnswerQuestion(value, sectionId) {
    setSections((prevSections) =>
      prevSections.map((section) =>
        section.id === sectionId ? { ...section, content: value } : section
      )
    );
  }

  async function handleCreateReflection() {
    try {
      const newReflection = await upsertReflection({
        reflectionText,
        author: userDetails.name,
        contextId: section.id,
      });
      setReflections((prevList) => [newReflection, ...prevList]);

      setClear(true);
      showNotification(
        t("notifications.general.createSuccess", {
          obj: t("reflections.reflection"),
        })
      );
    } catch {
      showNotification(
        t("notifications.general.createError", {
          obj: t("reflections.reflection"),
        }),
        ERROR
      );
    }
  }

  async function handleCreateReflectionFromTemplate() {
    try {
      const newReflection = await upsertReflectionFromTemplate({
        sections,
        author: userDetails.name,
        contextId: section.id,
        templateId: sectionRefTemplate.id,
      });
      setReflections((prevList) => [newReflection, ...prevList]);

      setSections(
        sectionRefTemplate.sections.map((section) => ({
          ...section,
          content: EMPTY_RTE,
        }))
      );
      setClear(true);
      showNotification(
        t("notifications.general.createSuccess", {
          obj: t("reflections.reflection"),
        })
      );
    } catch {
      showNotification(
        t("notifications.general.createError", {
          obj: t("reflections.reflection"),
        }),
        ERROR
      );
    }
  }

  async function handleSaveReflectionDraft() {
    try {
      const newReflection = await upsertReflectionDraft({
        reflectionText,
        contextId: section.id,
        author: userDetails.name,
        authorId: userDetails.canvas_id,
      });
      setReflections((prevList) => [newReflection, ...prevList]);

      setClear(true);
      showNotification(
        t("notifications.general.createSuccess", {
          obj: t("reflections.reflectionDraft"),
        })
      );
    } catch {
      showNotification(
        t("notifications.general.createError", {
          obj: t("reflections.reflectionDraft"),
        }),
        ERROR
      );
    }
  }

  async function handleSaveReflectionDraftFromTemplate() {
    try {
      const newReflection = await upsertReflectionDraftFromTemplate({
        sections,
        author: userDetails.name,
        authorId: userDetails.canvas_id,
        contextId: section.id,
        templateId: sectionRefTemplate.id,
      });
      setReflections((prevList) => [newReflection, ...prevList]);

      setSections(
        sectionRefTemplate.sections.map((section) => ({
          ...section,
          content: EMPTY_RTE,
        }))
      );
      setClear(true);
      showNotification(
        t("notifications.general.createSuccess", {
          obj: t("reflections.reflectionDraft"),
        })
      );
    } catch {
      showNotification(
        t("notifications.general.createError", {
          obj: t("reflections.reflectionDraft"),
        }),
        ERROR
      );
    }
  }

  function handleAuthorSelect(selectedAuthors) {
    setSelectedAuthors(selectedAuthors);
  }

  function hasFilledOutReflection() {
    if (sectionRefTemplate) {
      if (sections.some((section) => section.content === EMPTY_RTE)) {
        showNotification(t("reflections.submitTemplateReflectionError"), ERROR);
        return false;
      } else {
        return true;
      }
    } else {
      if (reflectionText === EMPTY_RTE) {
        showNotification(t("reflections.submitReflectionError"), ERROR);
        return false;
      } else {
        return true;
      }
    }
  }

  async function publishingReflection() {
    if (hasFilledOutReflection()) {
      setPublishing(true);
      if (sectionRefTemplate?.id) {
        await handleCreateReflectionFromTemplate();
      } else {
        await handleCreateReflection();
      }
      setPublishing(false);
    }
  }

  async function savingDraft() {
    if (hasFilledOutReflection()) {
      setCreatingDraft(true);
      if (sectionRefTemplate?.id) {
        await handleSaveReflectionDraftFromTemplate();
      } else {
        await handleSaveReflectionDraft();
      }
      setCreatingDraft(false);
    }
  }

  function isFiltered(reflection) {
    return (
      selectedAuthors.length === 0 ||
      selectedAuthors.includes(reflection.metadata.author) ||
      (reflection.is_draft && selectedAuthors.includes(userDetails.name))
    );
  }

  return (
    <div
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
      }}
    >
      <ProgressStepper steps={getFinalizeSteps(t)} currentStep={2} />
      <div style={{ flex: 1, overflowY: "hidden" }}>
        <Flex gap="small" height="100%">
          <Flex.Item size="50%" height="100%">
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
                height: "100%",
              }}
            >
              <Heading level="h3" as="h2" border="bottom" margin="0 small">
                {t("finalizeCourse.createReflections")}
              </Heading>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.75rem",
                  overflowY: "auto",
                  padding: "0 0.75rem",
                }}
              >
                {sectionRefTemplate ? (
                  <Flex wrap="wrap" gap="large">
                    {sectionRefTemplate.sections.map((section) => (
                      <Flex.Item key={section.id} width="100%">
                        <Flex wrap="wrap" gap="small">
                          <Flex.Item width="100%">
                            <Text weight="bold">{section.header_question}</Text>
                          </Flex.Item>
                          {section.helper_text &&
                            section.helper_text !== EMPTY_RTE && (
                              <Flex.Item>
                                <CustomRTE
                                  id={`helper-text-${section.id}`}
                                  defaultValue={section.helper_text}
                                  readOnly
                                />
                              </Flex.Item>
                            )}
                          <Flex.Item width="100%">
                            <CustomRTE
                              id={`reflection-text-${section.id}`}
                              onChange={(value) =>
                                handleAnswerQuestion(value, section.id)
                              }
                              clear={clear}
                              setClear={setClear}
                            />
                          </Flex.Item>
                        </Flex>
                      </Flex.Item>
                    ))}
                  </Flex>
                ) : (
                  <CustomRTE
                    id="reflection-rte"
                    label={
                      <Text weight="bold">{t("reflections.reflection")}</Text>
                    }
                    onChange={handleReflectionChange}
                    clear={clear}
                    setClear={setClear}
                  />
                )}
                <div style={{ alignSelf: "end", padding: "0.75rem 0" }}>
                  <Button
                    disabled={publishing || creatingDraft}
                    onClick={savingDraft}
                    margin="0 small 0 0"
                  >
                    {creatingDraft ? (
                      <Spinner
                        renderTitle={t("global.pleaseWait")}
                        size="x-small"
                      />
                    ) : (
                      t("finalizeCourse.saveAsPrivate")
                    )}
                  </Button>
                  <Button
                    color="primary"
                    renderIcon={
                      publishing ? (
                        <Spinner
                          renderTitle={t("global.pleaseWait")}
                          size="x-small"
                        />
                      ) : (
                        IconAddLine
                      )
                    }
                    disabled={publishing || creatingDraft}
                    onClick={publishingReflection}
                  >
                    {t("reflections.publish")}
                  </Button>
                </div>
              </div>
            </div>
          </Flex.Item>
          <Flex.Item height="100%">
            <div style={{ height: "100%", borderRight: "1px solid #ccc" }} />
          </Flex.Item>
          <Flex.Item shouldGrow shouldShrink height="100%">
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
                height: "100%",
              }}
            >
              <Heading level="h3" as="h2" border="bottom" margin="0 small">
                {t("finalizeCourse.existingReflections")}
              </Heading>
              <div style={{ padding: "0 0.75rem" }}>
                <Selector
                  type={MULTI}
                  label={t("finalizeCourse.authors")}
                  options={authorSelections}
                  selectHandler={handleAuthorSelect}
                />
              </div>
              <div style={{ padding: "0.75rem", overflowY: "auto" }}>
                {reflections ? (
                  reflections.length > 0 ? (
                    <Flex wrap="wrap" gap="medium">
                      {reflections.map(
                        (reflection) =>
                          isFiltered(reflection) && (
                            <Flex.Item key={reflection.ddl_id} size="100%">
                              <ReflectionCard
                                reflection={reflection}
                                sectionId={section.id}
                                sectionName={section.name}
                                sectionRefTemplate={sectionRefTemplate}
                                setReflections={setReflections}
                              />
                            </Flex.Item>
                          )
                      )}
                    </Flex>
                  ) : (
                    <Alert>{t("finalizeCourse.createAReflection")}</Alert>
                  )
                ) : (
                  <>
                    <Skeleton
                      height="10rem"
                      style={{ marginBottom: "1.5rem" }}
                    />
                    <Skeleton
                      height="10rem"
                      style={{ marginBottom: "1.5rem" }}
                    />
                    <Skeleton
                      height="10rem"
                      style={{ marginBottom: "1.5rem" }}
                    />
                  </>
                )}
              </div>
            </div>
          </Flex.Item>
        </Flex>
      </div>
    </div>
  );
};

export default FinalizeReflections;
